import Seo from "@/components/Seo";
import { useLang, route } from "../i18n";
import { useCompany } from "../components/SiteContext";
import PageHero from "../components/PageHero";
import ContactForm from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { MapPin, Phone, Clock, ShieldCheck } from "lucide-react";
import { track } from "../lib/track";

const Contact = () => {
  const { lang, t } = useLang();
  const company = useCompany();
  const c = t.contactPage;
  const a = company.address;

  return (
    <>
      <Seo title={c.seo.title} siteName={company.name} description={c.seo.description} />
      <PageHero
        eyebrow={c.eyebrow}
        lines={[c.title]}
        copy={c.copy}
        breadcrumb={[{ label: t.nav.home, to: route(lang, "home") }, { label: t.nav.contact }]}
        testid="contact-hero"
      />

      <section className="bg-[#F4F0E8]" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid gap-16 lg:grid-cols-[1.5fr_1fr]">
            <Reveal>
              <ContactForm />
            </Reveal>

            <Reveal delay={0.15}>
              <aside className="space-y-10 lg:pl-8">
                <div>
                  <h2 className="eyebrow text-[#8a6f3e]">{c.aside.title}</h2>
                  <address className="mt-5 flex items-start gap-3 text-sm not-italic leading-relaxed text-[#171717]/70">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A46A]" strokeWidth={1.5} />
                    <span>
                      {a.street}, {a.suite}
                      <br />
                      {a.city}, {a.state} {a.zip}
                    </span>
                  </address>
                  <a
                    href={company.phoneHref}
                    data-testid="contact-phone"
                    onClick={() => track("phone_click", { location: "contact" })}
                    className="mt-4 flex items-center gap-3 text-sm text-[#171717] transition-colors hover:text-[#8a6f3e]"
                  >
                    <Phone className="h-4 w-4 text-[#C6A46A]" strokeWidth={1.5} />
                    {company.phone}
                  </a>
                  <p className="mt-4 flex items-center gap-3 text-sm text-[#171717]/70">
                    <Clock className="h-4 w-4 text-[#C6A46A]" strokeWidth={1.5} />
                    {company.officeHours.days[lang]} · {company.officeHours.open}–{company.officeHours.close}
                  </p>
                  <p className="mt-2 pl-7 text-xs text-[#171717]/45">{c.aside.note}</p>
                </div>

                <div className="border border-[#171717]/12 bg-[#EEE9DF] p-6">
                  <h3 className="flex items-center gap-2 text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[#8a6f3e]">
                    <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                    {c.aside.neverTitle}
                  </h3>
                  <p className="mt-3 text-xs leading-relaxed text-[#171717]/65">{c.aside.neverCopy}</p>
                </div>

                <div className="border border-[#171717]/10">
                  <iframe
                    title="Aviah Financial — Lake Nona office map"
                    src={a.mapsEmbed}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="aspect-square w-full grayscale-[60%] contrast-[1.05]"
                    data-testid="contact-map"
                  />
                </div>
              </aside>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
